import { Button, Loader } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { clientsApi } from "#/api/clientsApi";
import { EmptyState } from "#/components/ui/EmptyState";
import ReportSection from "#/pages/reports/components/ReportSection";
import PredictionHowItWorks from "./PredictionHowItWorks";
import PredictionSettingsPanel from "./PredictionSettingsPanel";

interface Props {
  clientId: number;
}

/**
 * TMA-admin-only "Logistics Prediction" tab on Client Detail. Left: how it
 * works, plain then technical, with a link to the client's Predicted
 * Deliveries page. Right: the client's prediction settings, Recompute now and
 * the users' feedback totals. The PRV engine it builds on has its own tab.
 */
const LogisticsPredictionTab = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clientQuery = useQuery({
    queryKey: ["client", clientId, "prediction"],
    queryFn: async () => (await clientsApi.getById(clientId)).data,
  });

  if (clientQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader color="var(--client-color)" />
      </div>
    );
  }
  if (!clientQuery.data) {
    return <EmptyState title={t("common.failedToProcess")} />;
  }

  return (
    <div className="grid min-h-0 grid-cols-1 gap-5 lg:grid-cols-[3fr_7fr]">
      <ReportSection
        title={t("clients.prediction.settings.title")}
        subtitle={t("clients.prediction.settings.subtitle")}
        tooltip={t("clients.prediction.description")}
      >
        <PredictionSettingsPanel
          clientId={clientId}
          client={clientQuery.data}
          onSaved={() => {
            void clientQuery.refetch();
            void queryClient.invalidateQueries({ queryKey: ["client", String(clientId)] });
            void queryClient.invalidateQueries({ queryKey: ["client", clientId, "prv"] });
            void queryClient.invalidateQueries({ queryKey: ["data-health", clientId] });
          }}
        />
      </ReportSection>

      <ReportSection
        title={t("clients.prediction.howItWorks.title")}
        subtitle={t("clients.prediction.howItWorks.subtitle")}
        rightSection={
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconExternalLink size={14} />}
            onClick={() => void navigate(`/fleet/predicted-deliveries?clientId=${clientId}`)}
          >
            {t("clients.prediction.openPage")}
          </Button>
        }
      >
        <PredictionHowItWorks client={clientQuery.data} />
      </ReportSection>
    </div>
  );
};

export default LogisticsPredictionTab;
